
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import  themen  from 'theme/styles/styled-colors';
import  * as theme  from 'react-syntax-highlighter/dist/esm/styles/hljs';
import SyntaxHighlighter from 'react-syntax-highlighter';
import Img from 'theme/components/shared/Img'


const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <h1 {...{
      "id": "apache-atlas-hook--bridge-for-apache-hive"
    }}>{`Apache Atlas Hook & Bridge for Apache Hive`}</h1>
    <h2 {...{
      "id": "hive-model"
    }}>{`Hive Model`}</h2>
    <p>{`Hive model includes the following types:`}</p>
    <ul>
      <li parentName="ul">{`Entity types:`}</li>
      <li parentName="ul">{`hive_db`}
        <ul parentName="li">
          <li parentName="ul">{`super-types: Asset`}</li>
          <li parentName="ul">{`attributes: qualifiedName, name, description, owner, clusterName, location, parameters, ownerName`}</li>
        </ul>
      </li>
      <li parentName="ul">{`hive_table`}
        <ul parentName="li">
          <li parentName="ul">{`super-types: DataSet`}</li>
          <li parentName="ul">{`attributes: qualifiedName, name, description, owner, db, createTime, lastAccessTime, comment, retention, sd, partitionKeys, columns, aliases, parameters, viewOriginalText, viewExpandedText, tableType, temporary`}</li>
          <li parentName="ul">{`hive_column`}
            <ul parentName="li">
              <li parentName="ul">{`super-types: DataSet`}</li>
              <li parentName="ul">{`attributes: qualifiedName, name, description, owner, type, comment, table`}</li>
            </ul>
          </li>
          <li parentName="ul">{`hive_storagedesc`}
            <ul parentName="li">
              <li parentName="ul">{`super-types: Referenceable`}</li>
              <li parentName="ul">{`attributes: qualifiedName, table, location, inputFormat, outputFormat, compressed, numBuckets, serdeInfo, bucketCols, sortCols, parameters, storedAsSubDirectories`}</li>
            </ul>
          </li>
          <li parentName="ul">{`hive_process`}
            <ul parentName="li">
              <li parentName="ul">{`super-types: Process`}</li>
              <li parentName="ul">{`attributes: qualifiedName, name, description, owner, inputs, outputs, startTime, endTime, userName, operationType, queryText, queryPlan, queryId, clusterName`}</li>
            </ul>
          </li>
          <li parentName="ul">{`hive_column_lineage`}
            <ul parentName="li">
              <li parentName="ul">{`super-types: Process`}</li>
              <li parentName="ul">{`attributes: qualifiedName, name, description, owner, inputs, outputs, query, depenendencyType, expression`}</li>
            </ul>
          </li>
        </ul>
      </li>
    </ul>
    <ul>
      <li parentName="ul">{`Enum types:`}</li>
      <li parentName="ul">{`hive_principal_type`}
        <ul parentName="li">
          <li parentName="ul">{`values: USER, ROLE, GROUP`}</li>
        </ul>
      </li>
    </ul>
    <ul>
      <li parentName="ul">{`Struct types:`}
        <ul parentName="li">
          <li parentName="ul">{`hive_order`}
            <ul parentName="li">
              <li parentName="ul">{`attributes: col, order`}</li>
            </ul>
          </li>
          <li parentName="ul">{`hive_serde`}
            <ul parentName="li">
              <li parentName="ul">{`attributes: name, serializationLib, parameters`}</li>
            </ul>
          </li>
        </ul>
      </li>
    </ul>
    <p>{`Hive entities are created and de-duped in Atlas using unique attribute qualifiedName, whose value should be formatted as detailed below. Note that dbName, tableName and columnName should be in lower case.`}</p>
    <SyntaxHighlighter wrapLines={true} language="shell" style={theme.dark} mdxType="SyntaxHighlighter">
      {`hive_db.qualifiedName:     <dbName>@<clusterName>
hive_table.qualifiedName:  <dbName>.<tableName>@<clusterName>
hive_column.qualifiedName: <dbName>.<tableName>.<columnName>@<clusterName>
hive_process.queryString:  trimmed query string in lower case`}
    </SyntaxHighlighter>
    <h2 {...{
      "id": "hive-hook"
    }}>{`Hive Hook`}</h2>
    <p>{`Atlas Hive hook registers with Hive to listen for create/update/delete operations and updates the metadata in Atlas, via Kafka notifications, for the changes in Hive.
Follow the instructions below to setup Atlas hook in Hive:`}</p>
    <ul>
      <li parentName="ul">{`Set-up Atlas hook in hive-site.xml by adding the following:`}</li>
    </ul>
    <SyntaxHighlighter wrapLines={true} language="xml" style={theme.dark} mdxType="SyntaxHighlighter">
      {`<property>
    <name>hive.exec.post.hooks</name>
      <value>org.apache.atlas.hive.hook.HiveHook</value>
  </property>`}
    </SyntaxHighlighter>
    <ul>
      <li parentName="ul">{`untar apache-atlas-\${project.version}-hive-hook.tar.gz`}</li>
      <li parentName="ul">{`cd apache-atlas-hive-hook-\${project.version}`}</li>
      <li parentName="ul">{`Copy entire contents of folder apache-atlas-hive-hook-\${project.version}/hook/hive to `}<inlineCode parentName="li">{`<atlas package>`}</inlineCode>{`/hook/hive`}</li>
      <li parentName="ul">{`Add 'export HIVE_AUX_JARS_PATH=`}<inlineCode parentName="li">{`<atlas package>`}</inlineCode>{`/hook/hive' in hive-env.sh of your hive configuration`}</li>
      <li parentName="ul">{`Copy `}<inlineCode parentName="li">{`<atlas-conf>`}</inlineCode>{`/atlas-application.properties to the hive conf directory.`}</li>
    </ul>
    <p>{`The following properties in atlas-application.properties control the thread pool and notification details:`}</p>
    <SyntaxHighlighter wrapLines={true} language="shell" style={theme.dark} mdxType="SyntaxHighlighter">
      {`atlas.hook.hive.synchronous=false # whether to run the hook synchronously. false is recommended to avoid delays in Hive query completion. Default: false
atlas.hook.hive.numRetries=3      # number of retries for notification failure. Default: 3
atlas.hook.hive.queueSize=10000   # queue size for the threadpool. Default: 10000
atlas.cluster.name=primary # clusterName to use in qualifiedName of entities. Default: primary
atlas.kafka.zookeeper.connect=                    # Zookeeper connect URL for Kafka. Example: localhost:2181
atlas.kafka.zookeeper.connection.timeout.ms=30000 # Zookeeper connection timeout. Default: 30000
atlas.kafka.zookeeper.session.timeout.ms=60000    # Zookeeper session timeout. Default: 60000
atlas.kafka.zookeeper.sync.time.ms=20             # Zookeeper sync time. Default: 20`}
    </SyntaxHighlighter>
    <p>{`Other configurations for Kafka notification producer can be specified by prefixing the configuration name with "atlas.kafka.". For list of configuration supported by Kafka producer, please refer to `}<a parentName="p" {...{
        "href": "http://kafka.apache.org/documentation/#producerconfigs"
      }}>{`Kafka Producer Configs`}</a></p>
    <h2 {...{
      "id": "column-level-lineage"
    }}>{`Column Level Lineage`}</h2>
    <p>{`Starting from 0.8-incubating version of Atlas, Column level lineage is captured in Atlas. Below are the details`}</p>
    <h3 {...{
      "id": "model"
    }}>{`Model`}</h3>
    <ul>
      <li parentName="ul">{`ColumnLineageProcess type is a subtype of Process`}</li>
      <li parentName="ul">{`This relates an output Column to a set of input Columns or the Input Table`}</li>
      <li parentName="ul">{`The lineage also captures the kind of dependency, as listed below:`}
        <ul parentName="li">
          <li parentName="ul">{`SIMPLE:     output column has the same value as the input`}</li>
          <li parentName="ul">{`EXPRESSION: output column is transformed by some expression at runtime (for e.g. a Hive SQL expression) on the Input Columns.`}</li>
          <li parentName="ul">{`SCRIPT:     output column is transformed by a user provided script.`}</li>
        </ul>
      </li>
      <li parentName="ul">{`In case of EXPRESSION dependency the expression attribute contains the expression in string form`}</li>
      <li parentName="ul">{`Since Process links input and output DataSets, Column is a subtype of DataSet`}</li>
    </ul>
    <h3 {...{
      "id": "examples"
    }}>{`Examples`}</h3>
    <p>{`For a simple CTAS below:`}</p>
    <SyntaxHighlighter wrapLines={true} language="sql" style={theme.dark} mdxType="SyntaxHighlighter">
create table t2 as select id, name from T1
    </SyntaxHighlighter>
    <p>{`The lineage is captured as`}</p>
    <Img src={`/images/column_lineage_ex1.png`} height="200" width="400" mdxType="Img" />
    <h3 {...{
      "id": "extracting-lineage-from-hive-commands"
    }}>{`Extracting Lineage from Hive commands`}</h3>
    <ul>
      <li parentName="ul">{`The HiveHook maps the LineageInfo in the HookContext to Column lineage instances`}</li>
      <li parentName="ul">{`The LineageInfo in Hive provides column-level lineage for the final FileSinkOperator, linking them to the input columns in the Hive Query`}</li>
    </ul>
    <h2 {...{
      "id": "notes"
    }}>{`NOTES`}</h2>
    <ul>
      <li parentName="ul">{`Column level lineage works with Hive version 1.2.1 after the patch for `}<a href="https://issues.apache.org/jira/browse/HIVE-13112">{`HIVE-13112`}</a>{` is applied to Hive source`}</li>
      <li parentName="ul">{`Since database name, table name and column names are case-insensitive in hive, the corresponding names in entities are lowercase. So, any search APIs should use lowercase while querying on the entity names`}</li>
      <li parentName="ul">{`The following hive operations are captured by hive hook currently`}
        <ul parentName="li">
          <li parentName="ul">{`create database`}</li>
          <li parentName="ul">{`create table/view, create table as select`}</li>
          <li parentName="ul">{`load, import, export`}</li>
          <li parentName="ul">{`DMLs (insert)`}</li>
          <li parentName="ul">{`alter database`}</li>
          <li parentName="ul">{`alter table (skewed table information, stored as, protection is not supported)`}</li>
          <li parentName="ul">{`alter view`}</li>
        </ul>
      </li>
    </ul>
    <h2 {...{
      "id": "importing-hive-metadata"
    }}>{`Importing Hive Metadata`}</h2>
    <p>{`Apache Atlas provides a command-line utility, import-hive.sh, to import metadata of Apache Hive databases and tables into Apache Atlas.
This utility can be used to initialize Apache Atlas with databases/tables present in Apache Hive.
This utility supports importing metadata of a specific table, tables in a specific database or all databases and tables.`}</p>
    <SyntaxHighlighter wrapLines={true} language="shell" style={theme.dark} mdxType="SyntaxHighlighter">
      {`Usage 1: <atlas package>/hook-bin/import-hive.sh
Usage 2: <atlas package>/hook-bin/import-hive.sh [-d <database regex> OR --database <database regex>] [-t <table regex> OR --table <table regex>]
Usage 3: <atlas package>/hook-bin/import-hive.sh [-f <filename>]
           File Format:
             database1:tbl1
             database1:tbl2
             database2:tbl1`}
    </SyntaxHighlighter>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;